import React, { useState } from 'react';
import CTA from '../components/CTA';
import { IMAGES } from '../constants/images';
import useIsMobile from '../utils/useIsMobile';
import {
  FiShield, FiEye, FiShare2, FiToggleRight,
  FiLock, FiUser, FiLink, FiMail, FiChevronDown,
} from 'react-icons/fi';

const DotGrid = () => (
  <div aria-hidden style={{
    position: 'absolute', inset: 0, zIndex: 0,
    backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.12) 1px, transparent 1px)',
    backgroundSize: '28px 28px',
  }} />
);

const sections = [
  {
    icon: FiEye,
    title: '1. Information We Collect',
    color: '#3b82f6', bg: '#eff6ff',
    items: [
      'Personal information: name, contact details, and service address',
      'Technical information: IP address, device type, browser, usage data',
      'Communications: emails, messages, and call records for your requests',
    ],
  },
  {
    icon: FiToggleRight,
    title: '2. How We Use Your Information',
    color: '#8b5cf6', bg: '#f5f3ff',
    items: [
      'Provide and manage services',
      'Send updates, offers, and notifications',
      'Improve website, services, and customer support',
      'Comply with legal and regulatory requirements',
    ],
  },
  {
    icon: FiShare2,
    title: '3. How We Share Your Information',
    color: '#0ea5e9', bg: '#f0f9ff',
    prose: 'To fulfill your request, we share the details you submit (such as name, contact information, and address) with the providers, authorized partners, or contractors needed to check availability, prepare a quote, and set up the services you ask about. We do not sell your personal information for money.',
    items: [
      'Service providers and authorized partners, so they can contact you and provide the plans or services you requested',
      'Vendors that help us operate, such as hosting, communications, payment, and support tools',
      'Legal or regulatory authorities where required by law, or to protect our rights, users, and services',
    ],
  },
  {
    icon: FiShield,
    title: '4. Cookies, Analytics & Advertising',
    color: '#10b981', bg: '#ecfdf5',
    prose: 'We and our partners use cookies and similar technologies — including analytics and advertising tools such as Google Analytics and Google Ads — to operate the site, measure performance, and deliver and measure advertising (including remarketing/retargeting).',
    items: [
      'Improve functionality and performance',
      'Understand site usage through analytics (for example, Google Analytics)',
      'Deliver and measure advertising, including Google Ads remarketing/retargeting',
      'You can manage cookies in your browser and opt out of ad personalization (e.g., Google Ads Settings, youradchoices.com)',
    ],
  },
  {
    icon: FiLock,
    title: '5. Security of Your Information',
    color: '#f59e0b', bg: '#fffbeb',
    prose: 'We use appropriate technical and organizational measures to protect personal data from unauthorized access, disclosure, alteration, or destruction.',
  },
  {
    icon: FiUser,
    title: '6. Your Rights',
    color: '#ec4899', bg: '#fdf2f8',
    items: [
      'Access, correct, or delete personal information',
      'Opt out of marketing communications',
      'Restrict or object to processing where applicable',
    ],
  },
  {
    icon: FiLink,
    title: '7. Third-Party Links',
    color: '#64748b', bg: '#f8fafc',
    prose: 'Our website may include links to third-party sites. Their privacy practices and content are their responsibility. Review their policies separately.',
  },
  {
    icon: FiUser,
    title: '8. Your California & State Privacy Rights',
    color: '#0ea5e9', bg: '#f0f9ff',
    prose: 'Depending on where you live (for example, California under the CCPA/CPRA, and other U.S. states with privacy laws), you may have additional rights regarding your personal information:',
    items: [
      'Know what personal information we collect, use, and share',
      'Request access to, correction of, or deletion of your personal information',
      'Opt out of the “sale” or “sharing” of personal information as defined by applicable law',
      'Not receive discriminatory treatment for exercising your privacy rights',
    ],
  },
  {
    icon: FiToggleRight,
    title: '9. Do Not Sell or Share My Personal Information',
    color: '#8b5cf6', bg: '#f5f3ff',
    prose: 'We do not sell your personal information for money. To the extent any sharing with partners is considered a “sale” or “sharing” under state law, you may opt out by emailing contact@internettvservice.com with the subject “Do Not Sell or Share.” We will honor verified requests as required by law.',
  },
  {
    icon: FiMail,
    title: '10. Contact',
    color: '#2563eb', bg: '#eff6ff',
    prose: 'For privacy questions or to exercise your rights, contact our support team at contact@internettvservice.com or call +1 (888) 880-2585. Mailing address: 13800 Coppermine Rd, Herndon, VA 20171.',
  },
];

const AccordionItem = ({ section, index, isOpen, onToggle, isMobile }) => {
  const Icon = section.icon;
  return (
    <div style={{
      borderRadius: 16,
      border: `1.5px solid ${isOpen ? section.color + '40' : '#e8edf5'}`,
      background: '#fff', overflow: 'hidden',
      boxShadow: isOpen ? `0 8px 32px ${section.color}18` : '0 2px 10px rgba(0,0,0,0.04)',
      transition: 'box-shadow 0.3s, border-color 0.3s',
    }}>
      <button
        onClick={() => onToggle(index)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 16,
          padding: isMobile ? '16px 16px' : '22px 28px',
          background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <div style={{
          width: isMobile ? 36 : 42, height: isMobile ? 36 : 42, borderRadius: 11, flexShrink: 0,
          background: section.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'transform 0.2s', transform: isOpen ? 'scale(1.08)' : 'scale(1)',
        }}>
          <Icon style={{ width: isMobile ? 15 : 18, height: isMobile ? 15 : 18, color: section.color }} />
        </div>
        <span style={{ flex: 1, fontWeight: 800, fontSize: isMobile ? '0.92rem' : '1.05rem', color: '#0f172a', letterSpacing: '-0.01em' }}>
          {section.title}
        </span>
        <FiChevronDown style={{
          width: 17, height: 17, color: '#94a3b8', flexShrink: 0,
          transition: 'transform 0.3s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
        }} />
      </button>

      <div style={{ display: 'grid', gridTemplateRows: isOpen ? '1fr' : '0fr', transition: 'grid-template-rows 0.35s ease' }}>
        <div style={{ overflow: 'hidden', minHeight: 0 }}>
          <div style={{ padding: isMobile ? '0 var(--cc-px) 20px var(--cc-px)' : '0 28px 24px', paddingLeft: isMobile ? 'var(--cc-px)' : 86 }}>
            {section.prose && (
              <p style={{ color: '#1f2937', lineHeight: 1.85, margin: section.items ? '0 0 14px' : 0, fontSize: isMobile ? '0.92rem' : '1rem' }}>
                {section.prose}
              </p>
            )}
            {section.items && (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {section.items.map((item, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: section.color, flexShrink: 0, marginTop: 7 }} />
                    <span style={{ color: '#334155', fontSize: isMobile ? '0.9rem' : '1rem', lineHeight: 1.75 }}>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const PrivacyPolicy = () => {
  const [openIndex, setOpenIndex] = useState(0);
  const isMobile = useIsMobile(768);

  const handleToggle = (i) => setOpenIndex(prev => prev === i ? null : i);

  return (
    <main style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif", background: '#f8f9fc' }}>

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <section style={{ position: 'relative', minHeight: isMobile ? 'clamp(300px, 65vh, 380px)' : 'clamp(400px, 55vh, 480px)', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        <img src={IMAGES.heroPrivacy} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.currentTarget.src = 'https://picsum.photos/1600/900?blur=2'; }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(120deg, rgba(10,20,60,0.88) 0%, rgba(10,20,60,0.62) 55%, rgba(10,20,60,0.28) 100%)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(10,20,60,0.5) 0%, transparent 40%)' }} />
        <DotGrid />

        <div style={{ position: 'relative', zIndex: 1, width: '100%', padding: isMobile ? 'clamp(48px, 10vw, 64px) var(--cc-px) clamp(32px, 6vw, 44px)' : 'clamp(72px, 8vw, 100px) var(--cc-px) 70px', textAlign: 'center' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)', borderRadius: 999,
            padding: '6px 18px', marginBottom: 22,
          }}>
            <FiShield style={{ color: '#60a5fa', width: 15, height: 15 }} />
            <span style={{ color: 'rgba(255,255,255,0.95)', fontSize: '0.82rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Privacy &amp; Data</span>
          </div>

          <h1 style={{ color: '#fff', fontSize: 'clamp(2rem, 5vw, 6rem)', fontWeight: 900, lineHeight: 1.1, margin: '0 0 16px', letterSpacing: '-0.02em' }}>
            Privacy <span style={{ color: '#60a5fa' }}>Policy</span>
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.88)', fontSize: isMobile ? '0.97rem' : '1.15rem', maxWidth: 620, margin: '0 auto 22px', lineHeight: 1.85 }}>
            Your privacy matters. This page explains the data we collect, how it's used, and the choices you have.
          </p>
          <div style={{
            display: 'inline-block', background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.18)', borderRadius: 999,
            padding: '6px 18px', color: 'rgba(255,255,255,0.9)', fontSize: '0.83rem', fontWeight: 700,
          }}>
            Last updated: 2026
          </div>
        </div>
      </section>

      {/* ── TRUST PILLARS ────────────────────────────────────────────── */}
      <section style={{ padding: isMobile ? 'clamp(24px, 5vw, 36px) var(--cc-px) 0' : 'clamp(48px, 5vw, 72px) var(--cc-px) 0' }}>
        <div className="cc-narrow" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 14 }}>
          {[
            { icon: FiEye,   color: '#3b82f6', bg: '#eff6ff', title: 'Transparency', desc: "Clear policies outlining what we collect and how it's used." },
            { icon: FiLock,  color: '#10b981', bg: '#ecfdf5', title: 'Security',      desc: 'Technical and organizational controls to protect your data.' },
            { icon: FiUser,  color: '#8b5cf6', bg: '#f5f3ff', title: 'Control',       desc: 'Simple ways to access, correct, or delete your data anytime.' },
          ].map(({ icon: Icon, color, bg, title, desc }, i) => (
            <div key={i} style={{
              background: '#fff', borderRadius: 18, padding: '24px 20px',
              border: '1.5px solid #e8edf5', boxShadow: '0 2px 16px rgba(0,0,0,0.04)',
              transition: 'transform 0.22s, box-shadow 0.22s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 12px 32px ${color}22`; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 16px rgba(0,0,0,0.04)'; }}
            >
              <div style={{ width: 46, height: 46, borderRadius: 12, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                <Icon style={{ width: 19, height: 19, color }} />
              </div>
              <p style={{ fontWeight: 800, color: '#0f172a', fontSize: '1rem', margin: '0 0 7px' }}>{title}</p>
              <p style={{ color: '#64748b', fontSize: '0.86rem', lineHeight: 1.65, margin: 0 }}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── ACCORDION SECTIONS ───────────────────────────────────────── */}
      <section style={{ padding: isMobile ? 'clamp(24px, 5vw, 36px) var(--cc-px) clamp(40px, 8vw, 64px)' : 'clamp(40px, 4vw, 64px) var(--cc-px) 80px', maxWidth: 'var(--content-narrow)', margin: '0 auto' }}>
        {/* Intro */}
        <div style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)',
          borderRadius: 18, padding: isMobile ? 'clamp(20px, 4vw, 28px) var(--cc-px)' : '28px 32px', marginBottom: 28,
          position: 'relative', overflow: 'hidden',
        }}>
          <div aria-hidden style={{ position: 'absolute', top: -50, right: -50, width: 160, height: 160, borderRadius: '50%', border: '36px solid rgba(255,255,255,0.05)' }} />
          <FiShield style={{ width: 20, height: 20, color: '#60a5fa', marginBottom: 10 }} />
          <p style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.75, margin: 0, fontSize: isMobile ? '0.9rem' : '0.95rem', maxWidth: 640 }}>
            This policy explains how we collect, use, and protect personal information when you visit our website, use our services, or contact us. Click any section below to expand it.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {sections.map((section, i) => (
            <AccordionItem
              key={i}
              section={section}
              index={i}
              isOpen={openIndex === i}
              onToggle={handleToggle}
              isMobile={isMobile}
            />
          ))}
        </div>
      </section>

      <CTA />
    </main>
  );
};

export default PrivacyPolicy;
